"use client";

import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { FilterRegistration } from "./types";

export interface RegisterFilterArguments {
  type: string;
  hasValue: boolean;
  /**
   * A string that changes only when the visible chip content changes. This —
   * not `chip`/`renderMenu` identity — drives re-registration and consumer
   * re-render. Build it from the chip's content (e.g. value ids + resolved
   * labels), NOT from object identity.
   */
  signature: string;
  chip: FilterRegistration["chip"];
  renderMenu: FilterRegistration["renderMenu"];
  menuOptions?: FilterRegistration["menuOptions"];
}

interface FilterBarContextValue {
  registrations: FilterRegistration[];
  register: (reg: FilterRegistration) => void;
  unregister: (type: string) => void;
  openFilter: string | undefined;
  openMenu: (type: string) => void;
  closeMenu: () => void;
}

const FilterBarContext = createContext<FilterBarContextValue | undefined>(
  undefined,
);

export function useFilterBarContext(): FilterBarContextValue {
  const value = useContext(FilterBarContext);
  if (!value) {
    throw new Error(
      "useFilterBarContext must be used within a FilterBarProvider",
    );
  }
  return value;
}

/**
 * Headless registration: a controller calls this to publish its chip + menu.
 *
 * `chip` and `renderMenu` may be fresh objects/closures every render — they are
 * read via refs, so their identity is irrelevant (this matters because URL-param
 * hooks like `useMultiValueParameter` return new arrays/callbacks each render).
 * Re-registration (and thus consumer re-render) is driven solely by `signature`,
 * which the caller builds to change only when the visible chip content changes.
 */
export function useRegisterFilter({
  type,
  hasValue,
  signature,
  chip,
  renderMenu,
  menuOptions,
}: RegisterFilterArguments): void {
  const { register, unregister } = useFilterBarContext();

  // Always-latest refs so the registered object reflects the current render
  // without forcing a re-register on every identity change.
  const chipReference = useRef(chip);
  chipReference.current = chip;
  const renderMenuReference = useRef(renderMenu);
  renderMenuReference.current = renderMenu;
  const menuOptionsReference = useRef(menuOptions);
  menuOptionsReference.current = menuOptions;

  // Layout effect runs before paint, so an active filter's chip appears in the
  // first painted frame rather than flashing in a render late. Keyed on
  // `signature` (+ type/hasValue) so content changes re-register and re-render
  // consumers, while stable content never loops.
  useLayoutEffect(() => {
    register({
      type,
      hasValue,
      get chip() {
        return chipReference.current;
      },
      renderMenu: (...args: Parameters<FilterRegistration["renderMenu"]>) =>
        renderMenuReference.current(...args),
      get menuOptions() {
        return menuOptionsReference.current;
      },
    });
    return () => unregister(type);
  }, [register, unregister, type, hasValue, signature]);
}

export function FilterBarProvider({ children }: { children: React.ReactNode }) {
  const [registry, setRegistry] = useState<Record<string, FilterRegistration>>(
    {},
  );
  const [openFilter, setOpenFilter] = useState<string | undefined>();

  const register = useCallback((reg: FilterRegistration) => {
    setRegistry((previous) => ({ ...previous, [reg.type]: reg }));
  }, []);

  const unregister = useCallback((type: string) => {
    setRegistry((previous) => {
      const { [type]: _removed, ...next } = previous;
      return next;
    });
  }, []);

  const openMenu = useCallback((type: string) => setOpenFilter(type), []);
  const closeMenu = useCallback(() => setOpenFilter(undefined), []);

  const value = useMemo<FilterBarContextValue>(
    () => ({
      registrations: Object.values(registry),
      register,
      unregister,
      openFilter,
      openMenu,
      closeMenu,
    }),
    [registry, register, unregister, openFilter, openMenu, closeMenu],
  );

  return (
    <FilterBarContext.Provider value={value}>
      {children}
    </FilterBarContext.Provider>
  );
}
